import { Component, Input, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, lastValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  Advertiser, Campaign, CampaignLocationFilter, CampaignTargetUser,
  CampaignsCampaignAdminApiClient, CampaignsCampaignLocationFilterAdminApiClient,
  LinkedCampaignDto, LinkedCampaigns
} from 'src/app/api/api-campaigns.service.generated';
import { LoadingAwareComponent } from 'src/app/common/utils/loading-aware.component';
import { AdvertiserLinkedCampaignsEditComponent } from '../advertiser-linked-campaigns-edit/advertiser-linked-campaigns-edit.component';
import { LocationDetailDialogComponent, LocationDetailRow } from './location-detail-dialog/location-detail-dialog.component';
import { ButtonComponent, ConfirmDialogModel } from 'wecheer-common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatProgressBar } from "@angular/material/progress-bar";
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-advertiser-linked-campaigns',
  templateUrl: './advertiser-linked-campaigns.component.html',
  styleUrls: ['./advertiser-linked-campaigns.component.scss'],
  imports: [
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatTooltipModule,
    ButtonComponent,
    MatProgressBar
  ]
})
export class AdvertiserLinkedCampaignsComponent extends LoadingAwareComponent {
  private campaignsClient = inject(CampaignsCampaignAdminApiClient);
  private locationFilterClient = inject(CampaignsCampaignLocationFilterAdminApiClient);
  private dialog = inject(MatDialog);

  _advertiser: Advertiser;
  get advertiser() { return this._advertiser; }
  @Input() set advertiser(value: Advertiser) {
    this._advertiser = value;
    this.refresh();
  }

  linkedCampaigns: LinkedCampaignDto[];
  campaigns: Campaign[];
  locationFilterMap: Record<string, CampaignLocationFilter> = {};
  staffCampaignStrategies = ['StaffObsolete', 'StaffPointStrategy', 'OpenerStrategy'];
  displayedColumns = ['name', 'type', 'campaigns', 'locations', 'actions'];

  campaignBadgeFn(campaign: Campaign): { text: string; cssClass: string } | null {
    if (!campaign) return null;
    if (campaign.isConsumerReferralStrategy) return { text: 'Consumer Ref', cssClass: 'badge-consumer-referral' };
    if (campaign.isReferralCampaignStrategy) return { text: 'Staff Ref', cssClass: 'badge-staff-referral' };
    if (this.staffCampaignStrategies.includes(campaign?.strategy?.name ?? '')
      || campaign?.strategy?.name === 'StaffInformationStrategy'
      || campaign?.targetUser === CampaignTargetUser.Staff)
      return { text: 'Staff', cssClass: 'badge-staff' };
    return { text: 'Consumer', cssClass: 'badge-consumer' };
  }

  /** returns sorted distinct org/outlet ids from all filters in a location filter */
  private extractFilterKey(lf: CampaignLocationFilter | null | undefined): string {
    if (!lf?.filters?.length) return '';
    const ids: string[] = [];
    for (const f of lf.filters) {
      if (f?.organizationIds) ids.push(...f.organizationIds);
      else if (f?.outletIds) ids.push(...f.outletIds);
      else if (f?.tags) ids.push(...(f.tags as any[]).map((t: any) => `${t.key}:${t.value}`));
    }
    return [...new Set(ids)].sort().join(',');
  }

  hasLocationDiff(group: LinkedCampaignDto): boolean {
    if (!group?.campaigns?.length || group.campaigns.length < 2) return false;
    const keys = group.campaigns.map(c => this.extractFilterKey(this.locationFilterMap[c.campaignId]));
    return new Set(keys).size > 1;
  }

  getLocationTooltip(group: LinkedCampaignDto): string {
    return this.getLocationRows(group)
      .map(r => `${r.campaignTitle}: ${r.locations}`)
      .join('\n');
  }

  getLocationRows(group: LinkedCampaignDto): LocationDetailRow[] {
    if (!group?.campaigns?.length) return [];
    const keys = group.campaigns.map(c => this.extractFilterKey(this.locationFilterMap[c.campaignId]));
    const hasDiff = new Set(keys).size > 1;
    return group.campaigns.map(c => {
      const lf = this.locationFilterMap[c.campaignId];
      const ids: string[] = [];
      if (lf?.filters?.length) {
        for (const f of lf.filters) {
          if (f?.organizationIds) ids.push(...f.organizationIds);
          else if (f?.outletIds) ids.push(...f.outletIds);
          else if (f?.tags) ids.push(...(f.tags as any[]).map((t: any) => `${t.key}:${t.value}`));
        }
      }
      return {
        campaignTitle: c.title,
        locations: ids.length ? ids.join(', ') : '(all locations)',
        hasDiff,
      } as LocationDetailRow;
    });
  }

  openLocationDetail(group: LinkedCampaignDto): void {
    const rows = this.getLocationRows(group);
    // Mark only rows that differ from the majority
    const keyCount: Record<string, number> = {};
    rows.forEach(r => { keyCount[r.locations] = (keyCount[r.locations] ?? 0) + 1; });
    const majorityKey = Object.entries(keyCount).sort((a, b) => b[1] - a[1])[0]?.[0];
    rows.forEach(r => { r.hasDiff = r.locations !== majorityKey; });
    this.dialog.open(LocationDetailDialogComponent, {
      width: '620px',
      data: { programName: group.name, rows },
    });
  }

  async refresh() {
    if (!this.advertiser)
      return;

    this.linkedCampaigns = await lastValueFrom(this.campaignsClient.getAdvertiserLinkedCampaigns(this.advertiser.id));

    const allCampaigns = await lastValueFrom(this.campaignsClient.getCampaignsForAdvertisers(this.advertiser.id));

    this.campaigns = allCampaigns
      .filter(c => !c.isConsumerReferralStrategy);

    // Fetch location filters for all campaigns in all groups in parallel
    const allCampaignIds = [...new Set(
      (this.linkedCampaigns ?? []).flatMap(g => (g.campaigns ?? []).map(c => c.campaignId))
    )];
    if (allCampaignIds.length) {
      const results = await lastValueFrom(
        forkJoin(allCampaignIds.map(id =>
          this.locationFilterClient.getByCampaignId(id).pipe(catchError(() => of(null)))
        ))
      );
      this.locationFilterMap = {};
      allCampaignIds.forEach((id, i) => {
        if (results[i]) this.locationFilterMap[id] = results[i];
      });
    } else {
      this.locationFilterMap = {};
    }
  }


  async onAdd() {
    const campaignAlreadyLinkedIds = this.linkedCampaigns
      .flatMap(campaign => campaign.campaigns.map(campaign => campaign.id));

    const filteredCampaigns = this.campaigns.filter(campaign => !campaignAlreadyLinkedIds.includes(campaign.id))

    const updatedCampaign = await AdvertiserLinkedCampaignsEditComponent.open(this.dialog, { title: 'Add', campaigns: filteredCampaigns });

    if (!updatedCampaign) return;

    this.isLoading = true;

    const newLinkedCampaigns = [...(this.linkedCampaigns || []), updatedCampaign]

    await lastValueFrom(this.campaignsClient.updateAdvertiserLinkedCampaigns(this.advertiser.id, newLinkedCampaigns));

    this.refresh();

    this.isLoading = false;
  }

  async onRemove(linkedCampaign: LinkedCampaigns) {
    const dialogModel = ConfirmDialogModel.danger(
      'This program includes active campaign. Are you sure you want to delete this program?',
      'Delete this program?'
    );
    const confirmResult = await dialogModel.show(this.dialog);
    if (!confirmResult) return;

    this.isLoading = true;
    const indexToRemove = this.linkedCampaigns.findIndex(item =>
      JSON.stringify(item) === JSON.stringify(linkedCampaign)
    );

    if (indexToRemove !== -1) {
      const updatedCampaigns = [
        ...this.linkedCampaigns.slice(0, indexToRemove),
        ...this.linkedCampaigns.slice(indexToRemove + 1)
      ];

      await lastValueFrom(this.campaignsClient.updateAdvertiserLinkedCampaigns(this.advertiser.id, updatedCampaigns));
    }
    this.refresh();
    this.isLoading = false;
  }

  async onEdit(currentLinkedCampaign: LinkedCampaignDto) {
    const campaignAlreadyLinkedIds = this.linkedCampaigns
      .filter(item => !(JSON.stringify(item) === JSON.stringify(currentLinkedCampaign)))
      .flatMap(campaign => campaign.campaigns.map(campaign => campaign.id));

    const filteredCampaigns = this.campaigns.filter(campaign => !campaignAlreadyLinkedIds.includes(campaign.id))

    const updatedCampaign = await AdvertiserLinkedCampaignsEditComponent.open(this.dialog, { title: 'Edit', campaigns: filteredCampaigns, initData: JSON.parse(JSON.stringify(currentLinkedCampaign)) });
    if (!updatedCampaign) return;

    this.isLoading = true;

    this.linkedCampaigns = this.linkedCampaigns.map(item =>
      JSON.stringify(item) === JSON.stringify(currentLinkedCampaign) ? updatedCampaign : item
    );

    await lastValueFrom(this.campaignsClient.updateAdvertiserLinkedCampaigns(this.advertiser.id, this.linkedCampaigns));

    this.isLoading = false;
    this.refresh();
  }

  displayFn(campaign: LinkedCampaignDto): string {
    return campaign?.campaigns?.length ?
      campaign.campaigns.map(item => item.title).join(', ') :
      '';
  }
}
